import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import TopBackNavBar from '../../components/BackNavBar';
import BodyText from '../../components/BodyText';
import amex from '../../resources/images/CreditCardLogos/amex.svg';
import discover from '../../resources/images/CreditCardLogos/discover.svg';
import visa from '../../resources/images/CreditCardLogos/visa.svg';
import mastercard from '../../resources/images/CreditCardLogos/mastercard.svg';
import { RedButton } from '../../components/Button';
import { CreditCardInfo } from '../../interfaces/CreditCardInfo';
import { PaymentInfo } from '../../interfaces/PaymentInfo';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import { ConfigContext } from '../../providers/ConfigContext';
import postCbsCharge from '../../utils/postCbsCharge';
import getRecaptcha from '../../utils/getRecaptcha';
import bookfair from '../../interfaces/bookfair-info';
import updateSessionInfo from '../../utils/updateSessionInfo';
import { FairBarInfo } from '../../interfaces/fairBar-info';
import LandingPageHeader from '../../components/LandingPageHeader/LandingPageHeader';
import FairInfoBar from '../../components/FairInfoBar';
import { Pages } from '../../App';
import { setTaxErrorData } from '../../DumbleData';
import { SaleResponseInfo } from '../../interfaces/sale-response-info';

interface Props {
	ccInfo: CreditCardInfo;
	pInfo: PaymentInfo;
	bfInfo: bookfair;
	setBookfairInfo: any;
	setTransactionId: any;
	isTaxExemptChecked: boolean;
	version: string;
	payoffAmount: BigNumber;
	payoffFairId: number;
	payoffFairInfo: FairBarInfo;
	setSaleResponseInfo: React.Dispatch<React.SetStateAction<SaleResponseInfo>>;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const [postSale, postPayoff] = postCbsCharge();
const [getCaptcha] = getRecaptcha();
const { getSessionInfo } = updateSessionInfo();

const getCreditCardType = (cardType: string) => {
	switch (cardType) {
		case 'American Express':
			return amex;
		case 'Discover':
			return discover;
		case 'Visa':
			return visa;
		default:
			return mastercard;
	}
};

const handleTaxDiscrepancy = async (apiBase: string, setBookfairInfo: any, setCurrentPage: React.Dispatch<React.SetStateAction<number>>) => {
	const success = await getSessionInfo(apiBase, setBookfairInfo);
	if (success) {
		setCurrentPage(Pages.NewPurchase);
	} else {
		setCurrentPage(Pages.Landing);
	}
};

const CreditCardFinal = ({
	ccInfo,
	pInfo,
	bfInfo,
	setBookfairInfo,
	setTransactionId,
	isTaxExemptChecked,
	version,
	payoffAmount,
	payoffFairId,
	setSaleResponseInfo,
	setCurrentPage,
	payoffFairInfo,
}: Props) => {
	const { apiBase, reCaptchaSiteKey, reCaptchaCheckboxKey } = useContext(ConfigContext);
	const [status, setStatus] = useState<number>(200);
	const [submitting, setSubmitting] = useState(false);
	const [checkboxActive, setCheckboxActive] = useState(false);

	const chargeAmount = version === 'payoff' ? payoffAmount.toNumber() : pInfo.amountDue.toNumber();

	const submitCharge = async () => {
		const { data, error } =
			version === 'payoff'
				? await postPayoff(apiBase, payoffFairId.toString(), {
						amount: chargeAmount,
						billToForename: ccInfo.firstName,
						billToSurname: ccInfo.lastName,
						transientTokenJwt: ccInfo.transientToken,
					})
				: await postSale(apiBase, {
						amount: chargeAmount,
						billToForename: ccInfo.firstName,
						billToSurname: ccInfo.lastName,
						tax: {
							amount: pInfo.tax.toNumber(),
							rate: bfInfo.taxRate.toNumber(),
							taxableSubtotal: isTaxExemptChecked ? 0 : pInfo.itemTotal.toNumber(),
							status: bfInfo.taxStatus,
							selectedExempt: isTaxExemptChecked,
						},
						transientTokenJwt: ccInfo.transientToken,
					});

		if (error) {
			setStatus(error.status ?? 500);
			setSubmitting(false);
			return;
		}

		if (data != null) {
			setSaleResponseInfo({
				parentTransactionId: data.transactionId || '0',
				amount: chargeAmount,
				tenders: {
					credit: chargeAmount,
				},
				maskedCard: data.maskedCard,
				cardType: data.cardType,
			});
			setTransactionId(data.transactionId);
			window._satellite.track('credit-card-submit');
			setCurrentPage(Pages.Confirmation);
		}
	};

	const handlePayment = (e: any) => {
		e.preventDefault();
		if (submitting) {
			return;
		}
		setSubmitting(true);
		window.grecaptcha.enterprise.ready(async () => {
			const token = await window.grecaptcha.enterprise.execute(reCaptchaSiteKey, { action: 'CHECKOUT' });
			const { data } = await getCaptcha(token, apiBase, false);
			if (!data) {
				setSubmitting(false);
				return;
			}
			if (data.status.toLowerCase() !== 'success') {
				setCheckboxActive(true);
				setSubmitting(false);
				return;
			}
			await submitCharge();
		});
	};

	const checkboxCallback = async (token: string) => {
		if (submitting) {
			return;
		}
		setSubmitting(true);
		const { data } = await getCaptcha(token, apiBase, true);
		if (!data || data.status.toLowerCase() !== 'success') {
			setSubmitting(false);
			return;
		}
		await submitCharge();
	};

	React.useEffect(() => {
		try {
			window.grecaptcha.enterprise.render('recaptchaContainer', {
				sitekey: reCaptchaCheckboxKey,
				action: 'CHECKOUT',
				callback: checkboxCallback,
			});
		} catch (error) {
			// reCAPTCHA may throw an error if the container has already been rendered
		}
	}, []);

	let errorMessage: React.ReactNode = null;
	if (status === 422) {
		setTaxErrorData(true);
		errorMessage = (
			<>
				{'There appears to be a discrepancy in the sales tax amount. In order to proceed, please '}
				<StyledLink onClick={() => handleTaxDiscrepancy(apiBase, setBookfairInfo, setCurrentPage)}>click here</StyledLink>
				{' to recalculate the sales tax.'}
			</>
		);
	} else if (status === 403) {
		errorMessage = 'You are not authorized to pay off this fair at this time. Please contact your fair chairperson.';
	} else if (status !== 200) {
		errorMessage = 'We are unable to process your credit card. Please use a different payment method or call 877-245-0903.';
	}

	return (
		<>
			{version === 'payoff' && (
				<>
					<LandingPageHeader />
					<FairInfoBar fairInfo={payoffFairInfo} />
				</>
			)}
			<TopBackNavBar setCurrentPage={setCurrentPage} title="Finalize Your Payment" location={Pages.CreditCardForm} />
			<CreditCardFinalPage>
				<ContentWrapper>
					<RowItemDiv>
						<ItemLabel>Amount</ItemLabel>
						<ItemValue>{formatCurrencyNumber(chargeAmount)}</ItemValue>
					</RowItemDiv>
					<HorizontalLineBreak />
					<CreditSummaryDiv>
						<StyledImg src={getCreditCardType(ccInfo.cardType)} alt="Credit Card" />
						<CreditSummaryText>
							<b>{ccInfo.cardType}</b> <br /> {ccInfo.firstName} {ccInfo.lastName}
						</CreditSummaryText>
					</CreditSummaryDiv>
					<HorizontalLineBreak />
					<RecaptchaContainer $checkboxActive={checkboxActive} className="g-recaptcha" id="recaptchaContainer"></RecaptchaContainer>
					<StyledRedButton disabled={status !== 200 || submitting} handleClick={handlePayment}>
						Submit Payment
					</StyledRedButton>
					{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
				</ContentWrapper>
			</CreditCardFinalPage>
		</>
	);
};

const RecaptchaContainer = styled.div<{ $checkboxActive?: boolean }>`
	display: ${(props) => (props.$checkboxActive ? 'block' : 'none')};
`;

const CreditCardFinalPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 280px;
`;

const HorizontalLineBreak = styled.hr`
	width: 100%;
	height: 1px;
	background: #d8d8d8;
	border: 0px solid #d8d8d8;
	margin: 0px;
`;

const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
	margin: 39px 0 25px 0;
`;

const ItemLabel = styled(BodyText)`
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	text-align: left;
	margin: 0;
`;

const ItemValue = styled(BodyText)`
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	text-align: right;
	margin: 0;
`;

const CreditSummaryDiv = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 100%;
	padding: 10px 20px 10px 10px;
	box-sizing: border-box;
	justify-content: space-between;
`;

const StyledImg = styled.img`
	width: 50px;
	height: 50px;
	margin-right: 35px;
	left: 0px;
`;

const CreditSummaryText = styled(BodyText)`
	font-size: 13px;
	font-style: normal;
	font-weight: 400;
	line-height: 15px;
	text-align: left;
	margin: 0;
	b {
		font-weight: 600;
	}
`;

const StyledRedButton = styled(RedButton)`
	margin: 40px 0 7px 0;
	width: 100%;
`;

const ErrorText = styled(BodyText)`
	font-size: 13px;
	font-style: normal;
	font-weight: 400;
	line-height: 15px;
	color: #e81111;
	text-align: left;
	margin-bottom: 20px;
`;

const StyledLink = styled.a`
	text-decoration: underline;
	cursor: pointer;
`;

export default CreditCardFinal;
